import { getCellEditor, getCellEditorParams } from "./cellEditorHandler";
import { cellRenderer, cellStyle } from "./gridCellHandlers";
import { Checkbox } from "@mui/material";
import { Box } from "@mui/material";
import CustomFloatingFilterfrom from "./CustomFloatingFilter";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedRows, toggleStatusChangeRow } from "../../../redux/reducers/manageProviderSlice";

const DisabledHeaderCheckbox = () => (
  <input type="checkbox" disabled style={{ cursor: "not-allowed" }} />
);

const CustomCheckboxRenderer = (props) => {
  const dispatch = useDispatch();
  const selectedRows = useSelector(state => state.manageProvider.selectedRows || []);
  const isChecked = selectedRows.includes(props.data.provider_Id);

  const handleChange = (e) => {
    dispatch(setSelectedRows({
      providerId: props.data.provider_Id,
      checked: e.target.checked,
      rowData: props.data,
    }));
  };

  return (
    <input
      type="checkbox"
      checked={isChecked}
      onChange={handleChange}
      onClick={e => e.stopPropagation()}
    />
  );
};

const ActivateCheckboxRenderer = (props) => {
  const dispatch = useDispatch();
  const statusChangeSelectedRows = useSelector(state => state.manageProvider.statusChangeSelectedRows || []);
  const isChecked = statusChangeSelectedRows.some(r => r.npi === props.data?.npi);

  const handleChange = () => {
    dispatch(toggleStatusChangeRow(props.data));
  };

  return (
    <Checkbox
      checked={isChecked}
      onChange={handleChange}
      onClick={e => e.stopPropagation()}
      size="small"
      sx={{
        padding: 0,
        "&.Mui-checked": { color: "#FF8200" },
        "& .MuiSvgIcon-root": { fontSize: 16, strokeWidth: 0.5, stroke: "currentColor" },
        color: "rgba(0,0,0,0.38)",
      }}
    />
  );
};

const StatusChangeHeaderComponent = () => (
  <span style={{ fontSize: "11px", fontWeight: 600, color: "#555" }}>Status</span>
);

const checkboxColumn = {
  headerName: "",
  field: "custom_checkbox",
  width: 50,
  minWidth: 50,
  maxWidth: 50,
  editable: false,
  pinned: "left",
  suppressMenu: true,
  menuTabs: [],
  cellRenderer: CustomCheckboxRenderer,
  headerComponent: DisabledHeaderCheckbox,
  cellStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const activateCheckboxColumn = {
  headerName: "Status",
  field: "activate_checkbox",
  width: 100,
  minWidth: 100,
  maxWidth: 100,
  editable: false,
  pinned: "left",
  suppressMenu: true,
  menuTabs: [],
  cellRenderer: ActivateCheckboxRenderer,
  headerComponent: StatusChangeHeaderComponent,
  cellStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export const generateColumns = ({
  headers = [],
  deleteEnabled,
  statusChangeEnabled,
  showSearchFields,
  providerActiveStatus = "active",
  propertyMappings = {}
}) => {
  return [
    ...(deleteEnabled ? [checkboxColumn] : []),
    ...(statusChangeEnabled ? [activateCheckboxColumn] : []),
    ...headers.map((header) => ({
      headerName: header?.label || "",
      wrapText: header.key[0] === 'groupEntity',
      autoHeight: header.key[0] === 'groupEntity',
      headerTooltip: header?.label && header?.label.length > 5 ? header?.label : null,
      field: Array.isArray(header?.key) ? header.key[0] : header?.key || "",
      sortable: header?.sortable ?? false,
      filter: header?.filterable,
      floatingFilter: showSearchFields,
      floatingFilterComponent: CustomFloatingFilterfrom,
      suppressFloatingFilterButton: { suppressFilterButton: true, debounceMs: 300, immediateRefresh: true },
      minWidth: header?.min_width || 140,
      editable: providerActiveStatus === "inactive" ? false : header?.editable,
      cellStyle: (params) => {
        const baseStyle = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          height: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        };

        if (header.key[0] === 'groupEntity') {
          return {
            ...baseStyle,
            whiteSpace: 'normal',
            alignItems: 'flex-start',
          };
        }

        return {
          ...baseStyle,
          ...cellStyle(params),
        };
      },
      cellRenderer,
      tooltipValueGetter: () =>
        header?.groupable ? "Click For More Details" : null,
      headerComponentParams: {
        template: `
          <div class="ag-cell-label-container" role="presentation">
            <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
            <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
            <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
              <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
              ${header?.mandatory
                ? ' <span style="color: #A70B00;">*</span>'
                : ""
              }
              <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
              <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
              <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
              <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
              <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
            </div>
          </div>`
      },
      cellEditor: getCellEditor(header),
      cellEditorParams: getCellEditorParams(header),
      valueGetter: (params) => {
        if (!params.data || !header?.key) return '';
        const value = params.data[header.key];
        if (value === undefined || value === null) return '';

        if (header.key[0] === 'license') {
          const licenseTypes = ['Medical', 'CDS', 'DEA'];
          for (const type of licenseTypes) {
            if (value[type] && value[type].length > 0) {
              return value[type][0]?.licenseNumber || '';
            }
          }
          return '';
        }
        if (header.key[0] === 'degree' || header.key[0] === 'address' || header.key[0] === 'billingAddress' || header.key[0] === 'mediCare') {
          if (Array.isArray(value)) {
            return value[0]?.degreeName || value[0]?.addressLineFirst || value[0]?.addressLineFirst || value[0]?.medicarePtan || '';
          }
          return value.licenseNumber || '';
        }

        if (Array.isArray(value)) {
          if (value.length === 0) return '';
          if (typeof value[0] === 'object') {
            const propertyToShow = propertyMappings[header.key] || 'name';
            return value[0][propertyToShow] || '';
          }
          return value[0];
        }

        if (typeof value === 'object' && value !== null) {
          const objectPropertyMap = {
            'license': 'licenseNumber',
            'personal': 'caqhId',
          };
          const propertyToShow = objectPropertyMap[header.key] || Object.keys(value)[0];
          return value[propertyToShow] || '';
        }

        return value;
      },
      ...header,
    })),
  ];
};
